import _ from "underscore";
import DriveView from "../../DriveView";
import { getStartOfDayUserTimezone, dstCalculator, getNextDayStart, getPreviousDayStart } from "../../utils/date";
import { getServerDate } from "../../utils/storage/date";
import dayjs from "../../utils/dayjs";
import SelectDialog from "../framework/selectDialog";
import AppView, { IAppView } from "../../app.view";
import pageHTML from "./navigation.tmpl";
import { ISelectListOptions } from "./selectList";
import { handleApi } from "../../utils/error";
import { isIphone } from "../../utils/native/nativeHelpers";

export interface INavigateDateOptions extends IAppView {
    numDays: number;
    callback: (date: Date) => void;
    language?: string;
    app: AppView;
    unbounded?: boolean;
}

interface IDay {
    formattedDate: string;
    dayInMs: number;
}

interface IDays {
    [dateHash: string]: IDay;
}

export default class NavigateDateMenu extends DriveView<Backbone.Model> {
    protected language?: string;
    private readonly template = _.template(pageHTML);
    private readonly callback: (date: Date) => void;
    private readonly numDays: number;
    private readonly days: IDays = {};
    private readonly isUnbounded: boolean;
    private isNextButtonActive = true;
    private isPrevButtonActive = true;
    private currentDay = getStartOfDayUserTimezone(getServerDate());
    private lastChangeDayTime = 0;
    private readonly debounceDelayMs = 100;

    constructor(options: INavigateDateOptions) {
        super(options);
        this.callback = options.callback;
        this.language = options.language;
        this.numDays = options.numDays;
        this.isUnbounded = options.unbounded || false;
        this.populateDays();
    }

    protected get isPrevActive() {
        return this.isPrevButtonActive;
    }

    protected get isNextActive() {
        return this.isNextButtonActive;
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .navigateButton": this.changeDay,
            "click #selectDateButton": this.openDatePicker
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        this.display(this.getTemplate()).catch(handleApi);
        return this;
    }

    setCurrentDay(date: Date) {
        this.currentDay = date;
    }

    protected populateDays() {
        let amountOfDays = this.numDays;
        let dateHash: string;
        let startOfDay = getStartOfDayUserTimezone(getServerDate());

        for (let i = 0; i < amountOfDays; i++) {
            dateHash = startOfDay.toISOString();

            this.days[dateHash] = {
                formattedDate: this.formatDay(startOfDay),
                dayInMs: startOfDay.getTime()
            };

            startOfDay = getPreviousDayStart(startOfDay, true);
        }
    }

    protected selectDay(id?: string, title?: string, topLabel?: string, earlyReturnCallBack?: (dateHash: string, index: number) => boolean, getIcon?: (dateHash: string) => string) {
        let selectDateDialog = new SelectDialog({
            id: `${ id || "" }date-selector-dialog`,
            translator: this.translator,
            title: title || "Date picker",
            language: this.language,
            minWidth: 300,
            selectCallback: (dateObj) => {
                selectDateDialog.remove();
                let newDate = new Date(dateObj.id);
                if (this.currentDay.getTime() === newDate.getTime()) {
                    return;
                }

                this.currentDay = newDate;
                this.render();
                this.callback(this.currentDay);
            },
            isUserNightMode: this.app.store.getState().data.nightMode
        });

        selectDateDialog.render(this.getListOfDays(earlyReturnCallBack, getIcon), topLabel);
    }

    protected changeDay(e) {
        // Debounce to prevent double-firing on iOS where both touchend and click events fire
        if (isIphone()) {
            const currentTime = Date.now();
            if (currentTime - this.lastChangeDayTime < this.debounceDelayMs) {
                return;
            }
            this.lastChangeDayTime = currentTime;
        }

        const increment = parseInt(e.currentTarget.getAttribute("data-increment"), 10);
        if (increment > 0 ? !this.isNextButtonActive : !this.isPrevButtonActive) {
            return;
        }

        if (this.isUnbounded) {
            this.currentDay = increment > 0 ? getNextDayStart(this.currentDay, true) : getPreviousDayStart(this.currentDay, true);
        } else {
            const dateHashKeys = Object.keys(this.days).sort();
            const currentHash = this.currentDay.toISOString();
            const currentIndex = dateHashKeys.indexOf(currentHash);
            const newHash = dateHashKeys[currentIndex + increment];
            this.currentDay = new Date(newHash);
        }

        this.render();
        this.callback(this.currentDay);
    }

    protected getDays() {
        return this.days;
    }

    protected getNumDays() {
        return this.numDays;
    }

    protected getTemplate() {
        if (this.isUnbounded) {
            this.isPrevButtonActive = true;
            let nextDay = getNextDayStart(this.currentDay, true);
            this.isNextButtonActive = nextDay.getTime() <= getStartOfDayUserTimezone(getServerDate()).getTime();
        } else {
            let dateHashKeys = Object.keys(this.days).sort();
            let currentHash = this.currentDay.toISOString();
            let currentIndex = dateHashKeys.indexOf(currentHash);
            this.isPrevButtonActive = currentIndex - 1 >= 0;
            this.isNextButtonActive = currentIndex + 1 < dateHashKeys.length;
        }

        return this.template({
            currentDate: this.isUnbounded ? this.formatDay(this.currentDay) : this.days[this.currentDay.toISOString()].formattedDate,
            isNextButtonActive: this.isNextButtonActive,
            isPrevButtonActive: this.isPrevButtonActive
        });
    }

    protected addDay(dateHash: string, formattedDate: string, dayInMs: number) {
        this.days[dateHash] = { formattedDate, dayInMs };
    }

    protected deleteDay(dateHash: string) {
        delete this.days[dateHash];
    }

    private getListOfDays (earlyReturnCallBack?: (dateHash: string, index: number) => boolean, getIcon?: (dateHash: string) => string) {
        let daySelectOptions: ISelectListOptions[] = [];
        let isSelected = (dayInMs: number) => dayInMs === this.currentDay.getTime();
        let dayStart = getStartOfDayUserTimezone(getServerDate());
        let today = this.formatDay(dayStart);
        let yesterday = this.formatDay(getPreviousDayStart(dayStart, true));
        let getName = (formattedDate: string) => {
            if (formattedDate === today) {
                return "Today";
            } else if (formattedDate === yesterday) {
                return "Yesterday";
            } else {
                return formattedDate;
            }
        };

        let retentionPeriodDates = Object.keys(this.days);
        for (let i = 0; i < retentionPeriodDates.length; i++) {
            let dateHash = retentionPeriodDates[i];
            let day = this.days[dateHash];

            if (earlyReturnCallBack && earlyReturnCallBack(dateHash, i)) {
                continue;
            }

            let name = getName(day.formattedDate);
            daySelectOptions.push({
                id: dateHash,
                name,
                iconHtml: getIcon && getIcon(dateHash) || "",
                isSelected: isSelected(day.dayInMs),
                translate: name === "Yesterday" || name === "Today"
            });
        }
        return daySelectOptions;
    }

    private openDatePicker() {
        this.selectDay();
    }

    private formatDay(startOfDay: Date) {
        return dayjs(dstCalculator.toLocalDateTime(startOfDay), undefined, this.language).format("ddd MMM D");
    }
}